import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Drawer, FieldText, FieldTextarea, FieldSelect } from "@/pages/Clients";
import { PlusIcon, PencilSimpleIcon, TrashIcon, PaperclipIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "geral", label: "Geral" },
  { value: "aluguel", label: "Aluguel" },
  { value: "energia", label: "Energia" },
  { value: "internet", label: "Internet" },
  { value: "peças", label: "Peças" },
  { value: "marketing", label: "Marketing" },
  { value: "salário", label: "Salário" },
  { value: "imposto", label: "Imposto" },
  { value: "outros", label: "Outros" },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ExpenseForm({ initial, onSave, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(
    initial || {
      description: "",
      category: "geral",
      value: 0,
      date: today,
      supplier: "",
      receipt: "",
    }
  );
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const handleReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setData({ ...data, receipt: await fileToBase64(file) });
  };

  const submit = async (e) => {
    e.preventDefault();
    await onSave({ ...data, value: Number(data.value) || 0 });
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
      <FieldText label="Descrição *" value={data.description} onChange={set("description")} required testid="expense-desc-input" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FieldSelect label="Categoria" value={data.category} onChange={set("category")} options={CATEGORIES} testid="expense-category-select" />
        <FieldText label="Valor (R$) *" type="number" step="0.01" value={data.value} onChange={set("value")} required testid="expense-value-input" />
        <FieldText label="Data *" type="date" value={(data.date || "").slice(0, 10)} onChange={set("date")} required testid="expense-date-input" />
      </div>
      <FieldText label="Fornecedor" value={data.supplier} onChange={set("supplier")} testid="expense-supplier-input" />
      <div>
        <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Nota fiscal / Recibo</span>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer text-sm transition border border-white/10">
          <PaperclipIcon size={16} /> Anexar arquivo
          <input type="file" accept="image/*,application/pdf" onChange={handleReceipt} className="hidden" data-testid="expense-receipt-input" />
        </label>
        {data.receipt && <span className="text-xs text-emerald-400 ml-3">Anexado ✓</span>}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition" data-testid="expense-cancel-btn">
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_15px_rgba(37,99,235,0.4)] transition" data-testid="expense-save-btn">
          Salvar
        </button>
      </div>
    </form>
  );
}

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/expenses");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/expenses/${editing.id}`, data);
        toast.success("Gasto atualizado");
      } else {
        await api.post("/expenses", data);
        toast.success("Gasto registrado");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch {
      toast.error("Erro ao salvar gasto");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este gasto?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Gasto removido");
    load();
  };

  const total = items.reduce((s, i) => s + (i.value || 0), 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader
        title="Gastos"
        subtitle="Controle suas despesas e fornecedores."
        actions={
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="new-expense-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            <PlusIcon size={16} weight="bold" /> Novo gasto
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Total registrado</div>
          <div className="font-display text-2xl font-bold text-rose-400 mt-1">{brl(total)}</div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Quantidade</div>
          <div className="font-display text-2xl font-bold mt-1">{items.length}</div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Categorias</div>
          <div className="font-display text-2xl font-bold mt-1">{new Set(items.map((i) => i.category)).size}</div>
        </div>
      </div>

      <div className="glass rounded-xl p-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-3 pr-4">Descrição</th>
                <th className="py-3 pr-4">Categoria</th>
                <th className="py-3 pr-4">Fornecedor</th>
                <th className="py-3 pr-4">Data</th>
                <th className="py-3 pr-4">Valor</th>
                <th className="py-3 pr-4">NF</th>
                <th className="py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`expense-row-${e.id}`}>
                  <td className="py-3 pr-4 font-medium">{e.description}</td>
                  <td className="py-3 pr-4">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-300 text-xs border border-blue-500/20 capitalize">
                      {e.category}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-400">{e.supplier || "—"}</td>
                  <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="py-3 pr-4 font-medium text-rose-400">{brl(e.value)}</td>
                  <td className="py-3 pr-4 text-slate-400">
                    {e.receipt ? (
                      <button
                        onClick={() => setPreview(e.receipt)}
                        className="text-blue-400 hover:text-blue-300 text-xs underline"
                      >
                        Ver anexo
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditing(e); setOpen(true); }}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-blue-400 transition"
                        data-testid={`edit-expense-${e.id}`}
                      >
                        <PencilSimpleIcon size={16} />
                      </button>
                      <button
                        onClick={() => remove(e.id)}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-rose-400 transition"
                        data-testid={`delete-expense-${e.id}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-slate-500">Nenhum gasto registrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer
        open={open}
        title={editing ? "Editar gasto" : "Novo gasto"}
        onClose={() => { setOpen(false); setEditing(null); }}
      >
        <ExpenseForm initial={editing} onSave={save} onCancel={() => { setOpen(false); setEditing(null); }} />
      </Drawer>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl h-[90vh] glass rounded-2xl overflow-hidden border border-white/10">
            
            <button
              onClick={() => setPreview(null)}
              className="absolute top-4 right-4 z-10 px-3 py-1 rounded-lg bg-black/50 hover:bg-black/70 text-white"
            >
              Fechar
            </button>

            {preview.startsWith("data:application/pdf") ? (
              <iframe
                src={preview}
                title="Preview PDF"
                className="w-full h-full bg-white"
              />
            ) : (
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-contain bg-black"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
